class FeaturedCollectionSwiper {
  constructor() {
    this.swipers = new Map();
    this.init();
  }

  init() {
    // Buscar todos los sliders de featured-collection que tengan habilitado el desktop slider
    const sliders = document.querySelectorAll('.slider-component-desktop');
    
    sliders.forEach((slider) => {
      // Verificar que realmente tenga habilitado el desktop slider
      const enableDesktopSlider = slider.dataset.enableDesktopSlider === 'true';
      if (enableDesktopSlider) {
        this.initSwiper(slider);
      }
    });

    // Escuchar cambios en el DOM para nuevas secciones
    this.observeDOMChanges();
  }

  initSwiper(sliderElement) {
    const sectionId = sliderElement.dataset.sectionId;
    
    // If a Swiper already exists for this section, destroy it
    if (this.swipers.has(sectionId)) {
      this.swipers.get(sectionId).destroy();
      this.swipers.delete(sectionId);
    }

    // Get configuration from data attributes
    const config = this.getSwiperConfig(sliderElement);
    
    // Buscar el contenedor de slides con clase única para evitar conflictos
    const slidesContainer = sliderElement.querySelector(`.featured-collection-swiper-${sectionId}`);
    
    if (!slidesContainer) {
      console.warn(`No se encontró el contenedor de slides para la sección ${sectionId}`);
      return;
    }

    try {
      // Crear nueva instancia de Swiper
      const swiper = new Swiper(slidesContainer, config);
      
      // Guardar referencia con prefijo único
      this.swipers.set(`featured-collection-${sectionId}`, swiper);
      
      // Agregar eventos personalizados
      this.addCustomEvents(swiper, sliderElement, sectionId);
      
      // console.log(`Featured Collection Swiper inicializado para la sección ${sectionId}`, config);
      
    } catch (error) {
      console.error(`Error al inicializar Featured Collection Swiper para la sección ${sectionId}:`, error);
    }
  }

  getSwiperConfig(sliderElement) {
    const sectionId = sliderElement.dataset.sectionId;
    
    // Get complete JSON configuration
    let swiperConfig = {};
    try {
      if (sliderElement.dataset.swiperConfig) {
        swiperConfig = JSON.parse(decodeURIComponent(sliderElement.dataset.swiperConfig));
      }
    } catch (error) {
      console.warn(`Error parsing swiper config for section ${sectionId}:`, error);
    }

    // Valores por defecto y desde data attributes
    const columnsDesktop = parseInt(sliderElement.dataset.columnsDesktop) || swiperConfig.columns_desktop || 4;
    const columnsMobile = parseInt(sliderElement.dataset.columnsMobile) || swiperConfig.columns_mobile || 2;
    // Importante: solo activar autoplay si explícitamente está en 'true'
    const enableAutoplay = sliderElement.dataset.enableAutoplay === 'true' && swiperConfig.enable_autoplay === true;
    const autoplayDelay = parseInt(sliderElement.dataset.autoplayDelay) || swiperConfig.autoplay_delay || 5000;
    const pauseOnHover = sliderElement.dataset.pauseOnHover === 'true' || swiperConfig.pause_on_hover === true;
    const enableLoop = sliderElement.dataset.enableLoop === 'true' || swiperConfig.enable_loop === true;
    const spaceBetween = parseInt(sliderElement.dataset.spaceBetween) || swiperConfig.space_between || 30;

    // Base configuration for featured-collection
    const config = {
      slidesPerView: 'auto',
      spaceBetween: spaceBetween,
      loop: enableLoop,
      centeredSlides: false,
      watchOverflow: true,
      observer: true,
      observeParents: true,
      noSwiping: true,
      noSwipingClass: 'swiper-no-swiping',
      navigation: {
        nextEl: sliderElement.querySelector('.slider-button--next-desktop'),
        prevEl: sliderElement.querySelector('.slider-button--prev-desktop'),
      },
      scrollbar: {
        el: sliderElement.querySelector(`.featured-collection-scrollbar-${sectionId}`),
        hide: false,
        draggable: true,
        dragSize: 'auto',
        snapOnRelease: true
      },
      mousewheel: { forceToAxis: true, releaseOnEdges: true },
      breakpoints: {
        0: {
          slidesPerView: columnsMobile,
          slidesPerGroup: 1,
          spaceBetween: Math.max(spaceBetween - 10, 10)
        },
        768: {
          slidesPerView: Math.min(columnsMobile + 1, columnsDesktop),
          slidesPerGroup: 1,
          spaceBetween: Math.max(spaceBetween - 5, 15)
        },
        1024: {
          slidesPerView: columnsDesktop,
          slidesPerGroup: 1,
          spaceBetween: spaceBetween,
          centeredSlides: false
        }
      },
      // Callbacks
      on: {
        init: (swiper) => {
          this.onSwiperInit(swiper, sliderElement, sectionId);
          // Force update to ensure proper width calculation
          setTimeout(() => {
            swiper.update();
          }, 100);
        },
        slideChange: (swiper) => {
          this.onSlideChange(swiper, sliderElement, sectionId);
        },
        resize: (swiper) => {
          swiper.update();
        }
      }
    };

    // Add autoplay if enabled
    if (enableAutoplay) {
      config.autoplay = {
        delay: autoplayDelay,
        disableOnInteraction: false,
        pauseOnMouseEnter: pauseOnHover
      };
    }

    return config;
  }

  addCustomEvents(swiper, sliderElement, sectionId) {
    // Custom event to update counters
    swiper.on('slideChange', () => {
      this.updateCounters(sliderElement, swiper);
    });

    // Event to pause/resume autoplay
    let swiperConfig = {};
    try {
      if (sliderElement.dataset.swiperConfig) {
        swiperConfig = JSON.parse(decodeURIComponent(sliderElement.dataset.swiperConfig));
      }
    } catch (error) {
      console.warn(`Error parsing swiper config for section ${sectionId}:`, error);
    }

    const pauseOnHover = sliderElement.dataset.pauseOnHover === 'true';
    
    if (pauseOnHover && swiper.autoplay) {
      sliderElement.addEventListener('mouseenter', () => {
        swiper.autoplay.stop();
      });
      
      sliderElement.addEventListener('mouseleave', () => {
        swiper.autoplay.start();
      });
    }

    // Add keyboard events for accessibility
    sliderElement.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') {
        swiper.slidePrev();
      } else if (event.key === 'ArrowRight') {
        swiper.slideNext();
      }
    });
  }

  onSwiperInit(swiper, sliderElement, sectionId) {
    // Update initial counters
    this.updateCounters(sliderElement, swiper);
    
    // Initialize React components within the slider
    if (window.initializeReactComponents) {
      window.initializeReactComponents(sliderElement);
    }
    
    // Emit custom event
    sliderElement.dispatchEvent(new CustomEvent('swiper:init', {
      detail: { swiper, sectionId }
    }));
  }

  onSlideChange(swiper, sliderElement, sectionId) {
    // Emit custom event
    sliderElement.dispatchEvent(new CustomEvent('swiper:slideChange', {
      detail: { 
        swiper, 
        sectionId,
        activeIndex: swiper.activeIndex,
        realIndex: swiper.realIndex
      }
    }));
  }

  updateCounters(sliderElement, swiper) {
    const currentCounter = sliderElement.querySelector('.slider-counter--current');
    const totalCounter = sliderElement.querySelector('.slider-counter--total');
    
    if (currentCounter) {
      currentCounter.textContent = swiper.realIndex + 1;
    }
    
    if (totalCounter) {
      totalCounter.textContent = swiper.slides.length;
    }
  }

  observeDOMChanges() {
    // Observe DOM changes to detect new sections
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            // Look for new sliders in the added node
            const newSliders = node.querySelectorAll ? 
              node.querySelectorAll('.slider-component-desktop') : 
              (node.classList && node.classList.contains('slider-component-desktop') ? [node] : []);
            
            newSliders.forEach((slider) => {
              this.initSwiper(slider);
              // Initialize React components in the new slider
              if (window.initializeReactComponents) {
                window.initializeReactComponents(slider);
              }
            });
          }
        });
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  // Public methods for external control
  getSwiper(sectionId) {
    return this.swipers.get(`featured-collection-${sectionId}`);
  }

  destroySwiper(sectionId) {
    const swiper = this.swipers.get(`featured-collection-${sectionId}`);
    if (swiper) {
      swiper.destroy();
      this.swipers.delete(`featured-collection-${sectionId}`);
    }
  }

  destroyAll() {
    this.swipers.forEach((swiper, sectionId) => {
      swiper.destroy();
    });
    this.swipers.clear();
  }

  // Method to reinitialize a specific slider
  reinitSwiper(sectionId) {
    const sliderElement = document.querySelector(`[data-section-id="${sectionId}"].slider-component-desktop`);
    if (sliderElement) {
      this.initSwiper(sliderElement);
    }
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', function() {
  // Check if Swiper is available
  if (typeof Swiper === 'undefined') {
    console.warn('Swiper is not loaded. Make sure to include the Swiper library.');
    return;
  }

  // Crear instancia global
  window.featuredCollectionSwiper = new FeaturedCollectionSwiper();
});

// Also initialize on Shopify events
document.addEventListener('shopify:section:load', function(event) {
  if (window.featuredCollectionSwiper) {
    const sliderElement = event.target.querySelector('.slider-component-desktop');
    if (sliderElement) {
      window.featuredCollectionSwiper.initSwiper(sliderElement);
    }
  }
  // Initialize React components in the loaded section
  if (window.initializeReactComponents) {
    window.initializeReactComponents(event.target);
  }
});

document.addEventListener('shopify:section:unload', function(event) {
  if (window.featuredCollectionSwiper) {
    const sectionId = event.target.querySelector('.slider-component-desktop')?.dataset.sectionId;
    if (sectionId) {
      window.featuredCollectionSwiper.destroySwiper(sectionId);
    }
  }
}); 